import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { CaretRight, ArrowLeft, CircleNotch, MapPin } from '@phosphor-icons/react';
import { statsApi } from '../lib/api';
import { Button } from './ui/button';

const LEVEL_LABEL = {
  zone: 'Region',
  state: 'State',
  city: 'City',
  product: 'Product',
};

/**
 * Hierarchical incident breakdown: Region (NSEW) → State → City → Product
 * Stacked bars: stolen / suspicious / counterfeit
 * Location rows: fly map to lat/lng + open external navigation.
 */
export default function IncidentGeoDrilldown({
  isDark = false,
  onFilterLocations,
  onFocusLocation,
  className = '',
}) {
  const [zone, setZone] = useState(null);
  const [state, setState] = useState(null);
  const [city, setCity] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (zone) params.zone = zone;
      if (state) params.state = state;
      if (city) params.city = city;
      const res = await statsApi.incidentGeo(params);
      setData(res.data);
      onFilterLocations?.(res.data?.locations || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load regional incident analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [zone, state, city, onFilterLocations]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = useMemo(() => {
    return (data?.items || []).map((item) => {
      const key = item.key || 'Unknown';
      return {
        name: key === 'Unknown' ? 'Unspecified location' : key,
        drillKey: key,
        stolen: item.stolen || 0,
        suspicious: item.suspicious || 0,
        counterfeit: item.counterfeit || 0,
        total: item.count || 0,
      };
    });
  }, [data]);

  const pinLocations = useMemo(
    () =>
      (data?.locations || []).filter(
        (l) => l.latitude != null && l.longitude != null,
      ),
    [data],
  );

  const drillInto = (key) => {
    if (!data?.next_level) return;
    if (data.level === 'zone') setZone(key);
    else if (data.level === 'state') setState(key);
    else if (data.level === 'city') setCity(key);
  };

  const goBack = () => {
    if (city) setCity(null);
    else if (state) setState(null);
    else if (zone) setZone(null);
  };

  const showOnMap = (loc) => {
    onFocusLocation?.(loc);
    const el = document.getElementById('dashboard-stolen');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openNavigate = (loc) => {
    const lat = Number(loc.latitude);
    const lng = Number(loc.longitude);
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const crumbs = [
    { label: 'All India', onClick: () => { setZone(null); setState(null); setCity(null); }, active: !zone },
  ];
  if (zone) {
    crumbs.push({
      label: zone,
      onClick: () => { setState(null); setCity(null); },
      active: !state,
    });
  }
  if (state) {
    crumbs.push({
      label: state,
      onClick: () => setCity(null),
      active: !city,
    });
  }
  if (city) {
    crumbs.push({ label: city, onClick: () => {}, active: true });
  }

  const totals = data?.totals || { count: 0, stolen: 0, suspicious: 0, counterfeit: 0 };
  const chartH = Math.max(220, Math.min(420, 48 + chartData.length * 36));

  return (
    <div className={className} data-testid="incident-geo-drilldown">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
            Regional incident breakdown
          </h3>
          <p className={`mt-0.5 text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Drill down: Region → State → City → Product. Use location rows to go to lat/lng on the map or navigate.
          </p>
        </div>
        {(zone || state || city) && (
          <Button type="button" size="sm" variant="outline" onClick={goBack}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Back
          </Button>
        )}
      </div>

      <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c.label}-${i}`}>
            {i > 0 && <CaretRight className={`h-3 w-3 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`} />}
            <button
              type="button"
              onClick={c.onClick}
              className={`rounded px-1.5 py-0.5 ${
                c.active
                  ? isDark
                    ? 'bg-brand-500/15 font-semibold text-brand-400'
                    : 'bg-brand-50 font-semibold text-brand-700'
                  : isDark
                    ? 'text-zinc-400 hover:text-zinc-200'
                    : 'text-zinc-500 hover:text-zinc-800'
              }`}
            >
              {c.label}
            </button>
          </React.Fragment>
        ))}
      </nav>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: 'Total', value: totals.count, color: isDark ? 'text-zinc-100' : 'text-zinc-900' },
          { label: 'Suspicious', value: totals.suspicious, color: 'text-orange-500' },
          { label: 'Counterfeit', value: totals.counterfeit, color: 'text-violet-500' },
          { label: 'Stolen', value: totals.stolen, color: 'text-red-500' },
        ].map((s) => (
          <div
            key={s.label}
            className={`rounded-xl border px-3 py-2 ${isDark ? 'border-zinc-700 bg-zinc-800/60' : 'border-slate-200 bg-slate-50'}`}
          >
            <p className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{s.label}</p>
            <p className={`text-lg font-semibold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-zinc-400">
          <CircleNotch className="h-6 w-6 animate-spin text-brand-500" />
        </div>
      ) : error ? (
        <p className="py-8 text-center text-sm text-red-500">{error}</p>
      ) : chartData.length === 0 ? (
        <div className={`flex h-40 flex-col items-center justify-center text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
          <MapPin className="mb-2 h-8 w-8 opacity-40" />
          <p>No incidents at this level yet</p>
        </div>
      ) : (
        <>
          <p className={`mb-2 text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            {LEVEL_LABEL[data?.level] || 'Breakdown'}
            {data?.next_level ? ' — click a bar or row to drill down' : ''}
          </p>
          <div style={{ width: '100%', height: chartH }}>
            <ResponsiveContainer>
              <BarChart
                layout="vertical"
                data={chartData}
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                onClick={(chartState) => {
                  const key =
                    chartState?.activePayload?.[0]?.payload?.drillKey ||
                    chartState?.activeLabel ||
                    chartState?.activePayload?.[0]?.payload?.name;
                  if (key) drillInto(key);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3f3f46' : '#e2e8f0'} horizontal={false} />
                <XAxis type="number" allowDecimals={false} stroke={isDark ? '#a1a1aa' : '#64748b'} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  stroke={isDark ? '#a1a1aa' : '#64748b'}
                  fontSize={11}
                  tick={{ fill: isDark ? '#d4d4d8' : '#334155' }}
                />
                <Tooltip
                  cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                  contentStyle={{
                    background: isDark ? '#18181b' : '#fff',
                    border: isDark ? '1px solid #3f3f46' : '1px solid #e2e8f0',
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="suspicious" name="Suspicious" stackId="a" fill="#f97316" cursor={data?.next_level ? 'pointer' : 'default'} />
                <Bar dataKey="counterfeit" name="Counterfeit" stackId="a" fill="#8b5cf6" cursor={data?.next_level ? 'pointer' : 'default'} />
                <Bar dataKey="stolen" name="Stolen" stackId="a" fill="#ef4444" cursor={data?.next_level ? 'pointer' : 'default'} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 max-h-48 space-y-1 overflow-y-auto">
            {chartData.map((row) => (
              <button
                key={row.drillKey || row.name}
                type="button"
                disabled={!data?.next_level}
                onClick={() => drillInto(row.drillKey || row.name)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  data?.next_level
                    ? isDark
                      ? 'hover:bg-zinc-800'
                      : 'hover:bg-slate-100'
                    : ''
                } ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}
              >
                <span className="truncate font-medium">{row.name}</span>
                <span className={`shrink-0 text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {row.suspicious} sus · {row.counterfeit} cf · {row.stolen} stolen · <strong>{row.total}</strong>
                  {data?.next_level ? ' →' : ''}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {pinLocations.length > 0 && (
        <div className={`mt-5 border-t pt-4 ${isDark ? 'border-zinc-800' : 'border-slate-200'}`}>
          <h4 className={`mb-2 text-sm font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
            Locations ({pinLocations.length})
          </h4>
          <p className={`mb-2 text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Click a row to fly the map to that lat/lng, or Navigate to open turn-by-turn directions.
          </p>
          <div className="max-h-56 space-y-2 overflow-y-auto">
            {pinLocations.slice(0, 40).map((loc, idx) => (
              <div
                key={loc.id || `${loc.qr_id}-${loc.timestamp}-${idx}`}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${
                  isDark ? 'bg-zinc-800/50' : 'bg-slate-50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => showOnMap(loc)}
                  className="min-w-0 flex-1 text-left"
                  data-testid="incident-goto-map"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                    <span className={`truncate font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                      {loc.product_name || loc.qr_id || 'Incident'}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${
                      loc.incident_type === 'suspicious'
                        ? 'bg-orange-500/15 text-orange-600'
                        : loc.incident_type === 'counterfeit'
                          ? 'bg-violet-500/15 text-violet-600'
                          : 'bg-red-500/15 text-red-600'
                    }`}>
                      {loc.incident_type || 'stolen'}
                    </span>
                  </div>
                  <p className={`mt-0.5 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
                    {loc.city && loc.city !== 'Unknown' ? ` · ${loc.city}` : ''}
                    {loc.state && loc.state !== 'Unknown' ? `, ${loc.state}` : ''}
                  </p>
                </button>
                <div className="flex shrink-0 gap-1.5">
                  <Button type="button" size="sm" variant="outline" onClick={() => showOnMap(loc)}>
                    Show on map
                  </Button>
                  <Button type="button" size="sm" onClick={() => openNavigate(loc)}>
                    Navigate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
